import { useAuth } from "@/contexts/auth";
import {
  createElementObject,
  createPathComponent,
  extendContext,
} from "@react-leaflet/core";
import {
  ClientEvents,
  Geofence,
  ReferencePoint,
  SafeZone,
  TrackerPosition,
  Vehicle,
} from "@/interfaces";
import { errorHandler } from "@/lib/utils";
import { LocationData } from "@/services/api/lastTrackerPosition";
import { userService } from "@/services/api/user";
import { format } from "date-fns";
import L from "leaflet";
import "leaflet.markercluster";
import { ChevronDown, Clipboard } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Marker, Polyline, Popup } from "react-leaflet";
import AnimationManager from "./AnimationManager";
import GeographicAreasLayer from "./GeographicAreasLayer";
import { ClusterIcon, EventIcon } from "./Icons";
import RouteMarkers from "./Markers/RouteMarkers";
import StopMarker from "./Markers/StopMarker";
import VehicleMarker from "./Markers/VehicleMarker";

// ponytail: stands in for react-leaflet-markercluster, whose only react-leaflet 5
// build is a 2025 RC; same shape as that library's wrapper.
const MarkerClusterGroup = createPathComponent<
  L.MarkerClusterGroup,
  L.MarkerClusterGroupOptions & { children?: React.ReactNode }
>(({ children: _children, ...options }, ctx) => {
  const group = new L.MarkerClusterGroup(options);
  return createElementObject(
    group,
    extendContext(ctx, { layerContainer: group }),
  );
});

export interface GeographicAreas {
  reference_points?: ReferencePoint[];
  safe_zones?: SafeZone[];
  geofences?: Geofence[];
}

interface IconsControllerProps {
  events: ClientEvents[] | undefined;
  vehicles: Vehicle[] | undefined;
  setSelectedVehicleId: (id: number) => void;
  positions: TrackerPosition[];
  showAnimation?: boolean;
  displayEventsAndMarkersOptions?: boolean;
  onVehicleClick?: (vehicleId: number) => void;
}

const driversColors = [
  "#46ABFF",
  "#C532D9",
  "#000000",
  "#ECEB00",
  "#FF8E00",
  "#0AFF00",
  "#ff0050",
  "#996600",
  "#687869",
];

const IconsController = ({
  events,
  vehicles,
  setSelectedVehicleId,
  positions,
  showAnimation = false,
  displayEventsAndMarkersOptions = false,
  onVehicleClick,
}: IconsControllerProps) => {
  const [showVehiclesList, setShowVehiclesList] = useState(false);
  const [, setShowLabels] = useState(true);
  const [showIcons, setShowIcons] = useState(false);
  const [showRefPointsNames, setShowRefPointsNames] = useState(false);
  const [refPointsNamesTouched, setRefPointsNamesTouched] = useState(false);
  const [showGeographicAreas, setShowGeographicAreas] = useState(true);
  const [showEvents, setShowEvents] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const [, setAddressInfo] = useState<LocationData>();
  const [, setSidebarOpen] = useState(false);
  const [geographicAreas, setGeographicAreas] = useState<GeographicAreas>();
  const { userId } = useAuth();

  let stopsCount = 0;
  const getLineColor = (point: TrackerPosition) => {
    return point.driver ? driversColors[point.driver.color] : "#ef4444";
  };

  const createPolylineSegments = () => {
    if (positions.length === 0) return [];

    const segments: {
      positions: [number, number][];
      color: string;
    }[] = [];

    let currentSegment: [number, number][] = [];
    let currentColor = getLineColor(positions[0]);

    positions.forEach((point, index) => {
      const position: [number, number] = [point.latitude, point.longitude];

      // If this is the first point, just add it
      if (index === 0) {
        currentSegment.push(position);
        return;
      }

      const newColor = getLineColor(point);

      // If color changes or it's the last point, create a new segment
      if (newColor !== currentColor || index === positions.length - 1) {
        // Add current point to complete the previous segment
        currentSegment.push(position);

        // Save the completed segment
        segments.push({
          positions: currentSegment,
          color: currentColor,
        });

        // Start new segment from this point
        currentSegment = [position];
        currentColor = newColor;
      } else {
        // Same color, just add to current segment
        currentSegment.push(position);
      }
    });

    return segments;
  };

  const polylineSegments = createPolylineSegments();
  const showVehicleRoute = polylineSegments.length > 0 && !isAnimating;

  useEffect(() => {
    const fetchGeographicAreas = async () => {
      try {
        const response = await userService.getGeographicAreas(
          userId as number,
          "all",
        );
        setGeographicAreas(response.data);
      } catch (error) {
        errorHandler(error, "Erro ao carregar áreas geográficas");
      }
    };

    fetchGeographicAreas();
  }, [userId]);

  /**
   * Determines which vehicles should be included in marker cluster
   * Excludes vehicles that are:
   * - Outside a safe zone AND
   * - Have open doors, trunk, or locks (likely in an alert state)
   */
  const getVehiclesToAddToMarkerCluster = (vehicles?: Vehicle[]): Vehicle[] => {
    if (!vehicles) return [];
    if (vehicles.length === 1) return vehicles;

    return vehicles.filter((vehicle) => {
      const hasOpenComponents =
        vehicle.last_position?.is_trunk_open ||
        vehicle.last_position?.is_door_open ||
        vehicle.last_position?.is_lock_open;

      const shouldExcludeFromBounds =
        !vehicle.inside_safe_zone && hasOpenComponents;

      return !shouldExcludeFromBounds;
    });
  };

  const handleAddressInfo = (info: LocationData) => {
    setAddressInfo(info);
    setSidebarOpen(true);
  };

  const vehiclesToClusterer = getVehiclesToAddToMarkerCluster(vehicles);

  return (
    <React.Fragment>
      {/* Geographic Areas */}
      {showGeographicAreas && geographicAreas && (
        <GeographicAreasLayer
          showRefPointsNames={showRefPointsNames}
          geographicAreas={geographicAreas}
          onToggleNames={
            refPointsNamesTouched ? undefined : setShowRefPointsNames
          }
        />
      )}

      {/* Positions markers */}
      <RouteMarkers
        positions={positions}
        showIcons={showIcons}
        vehicles={vehicles}
      />

      {/* Draw vehicle route */}
      {showVehicleRoute &&
        polylineSegments.map((segment, index) => (
          <Polyline
            positions={segment.positions}
            pathOptions={{
              color: segment.color,
              weight: 3,
            }}
            key={`${index}`}
          />
        ))}

      {/* Stop markers */}
      {!isAnimating &&
        positions.map((point, index) => {
          if (point.stop) {
            stopsCount++;
          }

          return (
            <StopMarker
              key={`stop-${index}`}
              position={point}
              index={index}
              stopsCount={stopsCount}
            />
          );
        })}

      {/* Events markers */}
      {!isAnimating &&
        showEvents &&
        events &&
        events.map((eventData, index) => {
          return (
            <Marker
              key={`event-${index}`}
              position={[eventData.latitude, eventData.longitude]}
              icon={EventIcon()}
            >
              <Popup>
                <div className="text-sm p-4 w-64">
                  <div className="mb-1 text-black text-center font-bold">
                    Evento
                  </div>
                  <div className="mb-1 text-black">
                    Descrição: {eventData.event_description}
                  </div>
                  <div className="mb-1 text-black">
                    Data:{" "}
                    {format(
                      new Date(eventData.date + " " + eventData.hour),
                      "dd/MM/yyyy HH:mm:ss",
                    )}
                  </div>
                  <div className="mb-1 text-black">
                    Velocidade: {eventData.speed} km/h
                  </div>
                  {eventData.driver && (
                    <div className="mb-1 text-black">
                      Condutor: {eventData.driver}
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}

      {/* Vehicles markers */}
      <MarkerClusterGroup
        showCoverageOnHover={false}
        iconCreateFunction={ClusterIcon}
        spiderfyOnMaxZoom={true}
        disableClusteringAtZoom={17}
        maxClusterRadius={60}
        zoomToBoundsOnClick={true}
      >
        {vehiclesToClusterer &&
          vehiclesToClusterer.map((vehicle, index) => {
            return (
              <VehicleMarker
                vehicle={vehicle}
                index={index}
                key={`vehicle-marker-cluster-${index}`}
                onAddressInfo={handleAddressInfo}
                onVehicleClick={onVehicleClick}
              />
            );
          })}
      </MarkerClusterGroup>

      {/* Vehicles without cluster */}
      {vehicles &&
        vehicles.map((vehicle, index) => {
          if (!vehiclesToClusterer.includes(vehicle)) {
            return (
              <VehicleMarker
                vehicle={vehicle}
                index={index}
                key={`vehicle-marker-${index}`}
                onAddressInfo={handleAddressInfo}
                onAlert={true}
                onVehicleClick={onVehicleClick}
              />
            );
          }
          return null;
        })}

      {showAnimation && positions.length && (
        <AnimationManager
          routeData={positions}
          setShowIcons={setShowIcons}
          setShowLabels={setShowLabels}
          setIsAnimating={setIsAnimating}
          isAnimating={isAnimating}
        />
      )}

      <div className="leaflet-bottom leaflet-right mb-2">
        <div className="leaflet-control leaflet-bar bg-white text-gray-900 p-2 rounded shadow-md space-y-2">
          {displayEventsAndMarkersOptions && (
            <>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="showIcons"
                  checked={showIcons}
                  onChange={(e) => {
                    setShowIcons(e.target.checked);
                    setShowLabels(e.target.checked);
                  }}
                  className="form-checkbox h-4 w-4 text-blue-600"
                />
                <label htmlFor="showIcons" className="text-sm">
                  Marcadores
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="showEvents"
                  checked={showEvents}
                  onChange={(e) => setShowEvents(e.target.checked)}
                  className="form-checkbox h-4 w-4 text-blue-600"
                />
                <label htmlFor="showEvents" className="text-sm">
                  Eventos
                </label>
              </div>
            </>
          )}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="showRefPoints"
              checked={showRefPointsNames}
              onChange={(e) => {
                setRefPointsNamesTouched(true);
                setShowRefPointsNames(e.target.checked);
              }}
              className="form-checkbox h-4 w-4 text-blue-600"
            />
            <label htmlFor="showRefPoints" className="text-sm">
              Títulos das cercas
            </label>
          </div>
          {geographicAreas && (
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="showGeographicAreas"
                checked={showGeographicAreas}
                onChange={(e) => setShowGeographicAreas(e.target.checked)}
                className="form-checkbox h-4 w-4 text-blue-600"
              />
              <label htmlFor="showGeographicAreas" className="text-sm">
                Desenho das cercas
              </label>
            </div>
          )}
        </div>
        {/* Vehicles Quick Panel Button */}
        {vehicles && vehicles.length > 1 && (
          <div className="leaflet-control leaflet-bar">
            <button
              className="bg-white text-gray-900 p-2 rounded shadow-md flex items-center gap-1 text-sm hover:bg-gray-100"
              onClick={() => setShowVehiclesList(!showVehiclesList)}
            >
              <Clipboard size={16} />
              <span>Veículos</span>
              <ChevronDown
                size={16}
                className={`transition-transform ${showVehiclesList ? "rotate-180" : ""}`}
              />
            </button>

            {/* Vehicles List Panel */}
            {showVehiclesList && (
              <div className="absolute bottom-12 right-0 bg-white text-gray-900 rounded shadow-md p-2 max-h-60 overflow-y-auto w-64">
                <div className="text-sm font-bold mb-2 border-b pb-1">
                  Veículos Monitorados
                </div>
                <div className="space-y-1">
                  {vehicles.map((vehicle) => {
                    const hasPosition = !!vehicle.last_position;
                    const isActive = vehicle.last_position?.ignition;

                    return (
                      <div
                        key={vehicle.id}
                        className="flex items-center p-1 hover:bg-gray-100 rounded cursor-pointer"
                        onClick={() => {
                          if (hasPosition) {
                            setSelectedVehicleId(vehicle.id);
                          }
                        }}
                      >
                        <div
                          className={`w-2 h-2 rounded-full mr-2 ${
                            !hasPosition
                              ? "bg-gray-400"
                              : isActive
                                ? "bg-green-500"
                                : "bg-red-500"
                          }`}
                        />
                        <div className="flex-1 truncate">{vehicle.label}</div>
                        {hasPosition && (
                          <div className="text-xs text-gray-500">
                            {vehicle.last_position.speed} km/h
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default IconsController;
